import React, { useState, useRef, useCallback, useEffect } from 'react';
import apiService from '../services/ApiService';

interface AvatarUploadProps {
  currentAvatarUrl?: string;
  currentPosition?: { x: number; y: number; scale: number };
  onAvatarUpdate: (avatarUrl: string | null, position?: { x: number; y: number; scale: number }) => void;
  onError: (error: string) => void;
}

export const AvatarUpload: React.FC<AvatarUploadProps> = ({
  currentAvatarUrl,
  currentPosition,
  onAvatarUpdate,
  onError
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentAvatarUrl || null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [position, setPosition] = useState(currentPosition || { x: 0, y: 0, scale: 1 });
  const [isPositioning, setIsPositioning] = useState(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setPreviewUrl(currentAvatarUrl || null);
    setPosition(currentPosition || { x: 0, y: 0, scale: 1 });
  }, [currentAvatarUrl, currentPosition]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (selectedFile && previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [selectedFile, previewUrl]);

  const validateFile = useCallback((file: File): boolean => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

    if (!allowedTypes.includes(file.type)) {
      onError('Please upload a JPEG, PNG, or WebP image');
      return false;
    }

    if (file.size > maxSize) {
      onError('File size must be less than 5MB');
      return false;
    }

    return true;
  }, [onError]);

  const handleFileSelect = useCallback((file: File) => {
    if (!validateFile(file)) return;

    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    setPosition({ x: 0, y: 0, scale: 1 });
    setIsPositioning(true);
  }, [validateFile]);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isPositioning) return;
    
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!dragStart || !isPositioning) return;

    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;
    
    setPosition(prev => ({ ...prev, x: newX, y: newY }));
  }, [dragStart, isPositioning]);

  const handleMouseUp = useCallback(() => {
    setDragStart(null);
  }, []);

  useEffect(() => {
    if (dragStart) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragStart, handleMouseMove, handleMouseUp]);

  const handleScaleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const scale = parseFloat(e.target.value);
    setPosition(prev => ({ ...prev, scale }));
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    try {
      const result = await apiService.uploadAvatar(selectedFile, position);
      setPreviewUrl(result.avatar_url);
      setSelectedFile(null);
      setIsPositioning(false);
      onAvatarUpdate(result.avatar_url, result.position);
    } catch (error: any) {
      onError(error.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await apiService.deleteAvatar();
      setPreviewUrl(null);
      setSelectedFile(null);
      setIsPositioning(false);
      setPosition({ x: 0, y: 0, scale: 1 });
      onAvatarUpdate(null);
    } catch (error: any) {
      onError(error.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelEdit = () => {
    if (selectedFile && previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedFile(null);
    setPreviewUrl(currentAvatarUrl || null);
    setPosition(currentPosition || { x: 0, y: 0, scale: 1 });
    setIsPositioning(false);
  };

  return (
    <div className="space-y-4">
      {/* Avatar Preview */}
      <div className="flex items-center space-x-4">
        <div className="relative">
          <div className="w-24 h-24 bg-[#161616] rounded-full overflow-hidden border-2 border-white/[0.10]">
            {previewUrl ? (
              <div 
                ref={previewRef}
                className={`w-full h-full relative ${isPositioning ? 'cursor-move' : ''}`}
                style={{ 
                  transform: `translate(${position.x}px, ${position.y}px) scale(${position.scale})`,
                  transformOrigin: 'center'
                }}
                onMouseDown={isPositioning ? handleMouseDown : undefined}
              >
                <img 
                  src={previewUrl.startsWith('blob:') ? previewUrl : apiService.getFileUrl(previewUrl) || previewUrl} 
                  alt="Avatar preview" 
                  className="w-full h-full object-cover"
                  draggable={false}
                />
              </div>
            ) : (
              <div className="w-full h-full bg-gradient-brand-tile flex items-center justify-center text-2xl font-bold text-white">
                ?
              </div>
            )}
          </div>
          
          {isPositioning && (
            <div className="absolute -top-2 -right-2 bg-orange-500 text-white text-xs px-2 py-1 rounded">
              Positioning
            </div>
          )}
        </div>

        <div className="flex-1">
          <h3 className="text-lg font-medium text-white mb-2">Profile Picture</h3>
          <p className="text-sm text-gray-400 mb-4">
            {previewUrl ? 'Change your profile picture' : 'Upload a profile picture to replace the default avatar'}
          </p>
          
          {/* File Upload Area - Only show when no avatar is set */}
          {!previewUrl && (
            <div
              className={`
                relative border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors
                ${isDragOver ? 'border-orange-400 bg-orange-900/15' : 'border-white/[0.10] hover:border-white/[0.18]'}
              `}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="space-y-2">
                <svg className="w-8 h-8 text-gray-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="text-sm text-gray-300">
                  Drop an image here or click to select
                </p>
                <p className="text-xs text-gray-500">
                  JPEG, PNG, WebP up to 5MB
                </p>
              </div>
            </div>
          )}

          {/* Change Avatar and Remove Buttons - Show when avatar is set */}
          {previewUrl && !isPositioning && (
            <div className="flex space-x-3">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="bg-orange-600 hover:bg-orange-700 disabled:bg-[#1a1a1a] text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>Change Picture</span>
              </button>
              
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="bg-red-600 hover:bg-red-700 disabled:bg-[#1a1a1a] text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Removing...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    <span>Remove</span>
                  </>
                )}
              </button>
            </div>
          )}

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={handleFileInputChange}
            className="hidden"
          />
        </div>
      </div>

      {/* Positioning Controls */}
      {isPositioning && (
        <div className="bg-[#161616] rounded-lg p-4 space-y-4">
          <h4 className="text-sm font-medium text-white">Position Your Image</h4>
          
          <div className="space-y-2">
            <label className="block text-sm text-gray-300">Scale: {position.scale.toFixed(1)}x</label>
            <input
              type="range"
              min="0.1"
              max="3"
              step="0.1"
              value={position.scale}
              onChange={handleScaleChange}
              className="w-full h-2 bg-[#1a1a1a] rounded-lg appearance-none cursor-pointer"
            />
          </div>
          
          <p className="text-xs text-gray-400">
            Drag the image above to reposition it. Use the scale slider to zoom in or out.
          </p>
        </div>
      )}

      {/* Action Buttons */}
      {isPositioning && (
        <div className="flex space-x-3">
          <button
            onClick={handleUpload}
            disabled={isUploading || !selectedFile}
            className="bg-orange-600 hover:bg-orange-700 disabled:bg-[#1a1a1a] text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
          >
            {isUploading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Uploading...</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Save Avatar</span>
              </>
            )}
          </button>
          
          <button
            onClick={cancelEdit}
            disabled={isUploading}
            className="bg-[#1a1a1a] hover:bg-white/[0.06] text-white px-4 py-2 rounded-lg transition-colors"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}; 